import React, { MutableRefObject } from "react";
import { _handleError } from "./scanViewErrorHandlerHelper";
import {
  generateImageFromCanvas,
  generateImageMetadata,
} from "./scanCanvasImageHelper";
import { isIOS } from "react-device-detect";
import { dataURLtoFile, calculateGuides } from "./helper";
import { Dispatch, SetStateAction } from "react";
import detectorWrapper from "./detector";
import { GlobalLogger } from "./debugLogger";
import {
  getDeviceInfo,
  getEnvironmentInfo,
  getRefSize,
} from "../utils/getInfoFunctions";
import { ModelPrediction } from "./types/Model";
import { CardScanApi } from "../types/types";
import { CaptureType, PerformanceData } from "../types/general.types";
import AdaptiveThreshold from "../utils/AdaptiveThreshold";
import { ScanGuidance, ScanGuidanceUI } from "../utils/ScanGuidance";

//Main scan processing handler for scans -- called every ~250ms when active.
//Handles: auto & manual scan

//---------------------------------------
// Hot Path
//---------------------------------------

export const scanHandler = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  detectionCanvasRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasLatestRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasBestRef: React.RefObject<HTMLCanvasElement>,
  scanCountRef: React.MutableRefObject<number>,
  performanceCountRef: React.MutableRefObject<number>,
  topPredictions: React.MutableRefObject<ModelPrediction[]>,
  getPerformanceData: () => PerformanceData,
  setLatestScore: Dispatch<SetStateAction<number>>,
  setLatestLaplacian: Dispatch<SetStateAction<number>>,
  setBestScore: Dispatch<SetStateAction<number>>,
  setBestClass: Dispatch<SetStateAction<ModelPrediction | null>>,
  setLastClass: Dispatch<SetStateAction<string>>,
  setBestLaplacian: Dispatch<SetStateAction<number>>,
  setModelState: Dispatch<SetStateAction<string>>,
  setScanDebugState: Dispatch<SetStateAction<string>>,
  setScanProgress: Dispatch<SetStateAction<number>>,
  flipHorizontal = false,
  flipVertical = false,
  scanCaptureBuffer: any,
  adaptiveThreshold: AdaptiveThreshold,
  onError: any,
  scanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoMetadata: any,
  cardScanApi: React.MutableRefObject<CardScanApi | null>,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  metricsDumpRef: MutableRefObject<string[]>,
  guidanceUI: MutableRefObject<ScanGuidanceUI>,
  scanGuidance: MutableRefObject<ScanGuidance>,
  handleGuidanceUI: (
    newGuideMessage: string,
    guideColor: "detected" | "notDetected"
  ) => void,
  debugMenu?: boolean
) => {
  const video = videoRefBg.current;
  if (!video || stateMachineState.matches("cameraLoading")) {
    GlobalLogger.error("scanHandler -- no video found");
    return;
  }
  if (!cardIdRef.current) {
    GlobalLogger.debug("scanHandler -- no card Id found");
    return;
  }

  const detectionCanvas = detectionCanvasRef.current;
  const captureCanvas = captureCanvasRef.current;
  const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
    videoRefBg,
    frameDivRef
  );

  GlobalLogger.debug(
    `Canvas: ${captureCanvas?.width}, ${captureCanvas?.height}`,
    "everything"
  );
  GlobalLogger.debug(
    `Video: ${video.videoWidth}, ${video.videoHeight}`,
    "everything"
  );

  if (!(detectionCanvas && captureCanvas)) {
    GlobalLogger.error("CardScan: no canvas found - critical failure");
    return;
  }

  //resize image to canvas sizes
  detectionCanvas
    .getContext("2d")
    ?.drawImage(
      video,
      guideX,
      guideY,
      guideWidth,
      guideHeight,
      0,
      0,
      detectionCanvas.width,
      detectionCanvas.height
    );

  let jpegUrl;

  //work around for iOS GPU memory issue.
  //TODO: check we still need this and remove if not. might have been fixed by useInterval
  if (!isIOS) {
    jpegUrl = generateImageFromCanvas(
      videoRefBg,
      frameDivRef,
      captureCanvasRef,
      flipHorizontal,
      flipVertical
    );
  }

  detectCard(
    videoRefBg,
    frameDivRef,
    captureCanvasRef,
    detectionCanvas,
    debugCanvasLatestRef,
    debugCanvasBestRef,
    scanCountRef,
    performanceCountRef,
    topPredictions,
    getPerformanceData,
    setLatestScore,
    setLatestLaplacian,
    setBestScore,
    setBestClass,
    setLastClass,
    setBestLaplacian,
    setModelState,
    setScanDebugState,
    flipHorizontal,
    flipVertical,
    adaptiveThreshold,
    onError,
    scanCaptureBuffer,
    scanProgress,
    setScanProgress,
    softMaxUploads,
    bufferSize,
    videoMetadata,
    cardScanApi,
    cardIdRef,
    stateMachineState,
    stateMachineSend,
    metricsDumpRef,
    guidanceUI,
    scanGuidance,
    handleGuidanceUI,
    jpegUrl,
    debugMenu
  );
};

const detectCard = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  detectionCanvas: HTMLCanvasElement,
  debugCanvasLatestRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasBestRef: React.RefObject<HTMLCanvasElement>,
  scanCountRef: React.MutableRefObject<number>,
  performanceCountRef: React.MutableRefObject<number>,
  topPredictions: React.MutableRefObject<ModelPrediction[]>,
  getPerformanceData: () => PerformanceData,
  setLatestScore: Dispatch<SetStateAction<number>>,
  setLatestLaplacian: Dispatch<SetStateAction<number>>,
  setBestScore: Dispatch<SetStateAction<number>>,
  setBestClass: Dispatch<SetStateAction<ModelPrediction | null>>,
  setLastClass: Dispatch<SetStateAction<string>>,
  setBestLaplacian: Dispatch<SetStateAction<number>>,
  setModelState: Dispatch<SetStateAction<string>>,
  setScanDebugState: Dispatch<SetStateAction<string>>,
  flipHorizontal: boolean,
  flipVertical: boolean,
  adaptiveThreshold: AdaptiveThreshold,
  onError: any,
  scanCaptureBuffer: any,
  scanProgress: any,
  setScanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoMetadata: any,
  cardScanApi: React.MutableRefObject<CardScanApi | null>,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  metricsDumpRef: MutableRefObject<string[]>,
  guidanceUI: MutableRefObject<ScanGuidanceUI>,
  scanGuidance: MutableRefObject<ScanGuidance>,
  handleGuidanceUI: (
    newGuideMessage: string,
    guideColor: "detected" | "notDetected"
  ) => void,
  jpegUrl?: string,
  debugMenu?: boolean
) => {
  detectorWrapper
    .get()
    .predict_image(detectionCanvas, flipHorizontal, flipVertical)
    .then((ret) => {
      performanceCountRef.current += 1;

      if (ret === null) {
        GlobalLogger.log("cardDetector race condition -- early exit");
        return;
      }
      // Save the performance data approximately every 2.5 seconds
      if (performanceCountRef.current % 10 === 0 && cardIdRef.current) {
        const performanceData = getPerformanceData();

        const orientation = ["backsideProcessing", "backsidePending"].some(
          stateMachineState.matches
        )
          ? "back"
          : "front";

        const payload = {
          ...ret,
          ...performanceData,
          orientation,
        };

        cardScanApi.current
          ?.cardPerformance(cardIdRef.current, payload)
          .catch((error: any) => {
            GlobalLogger.error(error);
          });
      }

      if (debugMenu) {
        setModelState(detectorWrapper.get().modelState);
        setScanDebugState("active");
      }

      const backsideMode =
        stateMachineState.matches("backsidePending") ||
        stateMachineState.matches("backsideProcessing");

      if (backsideMode && /^card_/.test(ret.modelClass)) {
        metricsDumpRef.current.push(
          JSON.stringify({
            ...ret,
            orientation: "back", // tag it now
            ts: Date.now(), // optional timestamp
          })
        );
        scanGuidance.current.record(ret.modelClass);
      }

      const frontsideScan =
        ["autoCapture", "frontsideProcessing", "manualCapture"].some(
          stateMachineState.matches
        ) && ret["modelClass"] === "card_front";

      const isCardX = ret.modelClass.startsWith("card_");
      const backsideScan =
        backsideMode &&
        (ret.modelClass === "card_back" ||
          (scanGuidance.current.needsRelaxation() && isCardX));

      const newPrediction = {
        score: ret.modelProbability,
        className: ret.modelClass,
      };

      GlobalLogger.log(
        `${newPrediction.className}:${newPrediction.score}`,
        "everything"
      );

      topPredictions.current.push(newPrediction);

      if (performanceCountRef.current % 4 === 0) {
        topPredictions.current.sort((a, b) => {
          return b.score - a.score;
        });
        const topPrediction = topPredictions.current[0];
        GlobalLogger.log(
          `topPrediction: ${topPrediction.className} - ${topPrediction.score}`,
          "verbose"
        );

        topPredictions.current = [];
      }

      setBestClass((prev) =>
        prev === null
          ? newPrediction
          : prev.score < newPrediction.score
          ? newPrediction
          : prev
      );

      setLastClass(newPrediction.className);

      const bypass =
        stateMachineState.context.photoScanning &&
        [
          "autoCapture",
          "manualCapture",
          "frontsideProcessing",
          "backsidePending",
          "backsideProcessing",
        ].some(stateMachineState.matches);

      const { message, color } = guidanceUI.current.get(ret);
      handleGuidanceUI(message, bypass ? "detected" : color);

      if (
        ((frontsideScan || backsideScan) &&
          stateMachineState.context.scannerRunning) ||
        bypass
      ) {
        const score = Number(ret["modelProbability"]);
        const laplacian = Number(ret["laplaceVariance"]);

        GlobalLogger.debug(`Score: ${score} Lap: ${laplacian}`, "detector");

        if (debugMenu) {
          setLatestScore(score);
          setLatestLaplacian(laplacian);
          renderDebugCanvas(
            videoRefBg,
            frameDivRef,
            debugCanvasLatestRef,
            debugMenu
          );
        }

        const { cardThreshold, laplacianThreshold } =
          adaptiveThreshold.getCurrent();

        const passed =
          (score > cardThreshold && laplacian > laplacianThreshold) || bypass;

        if (backsideScan || frontsideScan) {
          adaptiveThreshold.recordAttempt(score, laplacian, passed);
        }

        // Add quality images to the buffer...
        if (passed) {
          if (
            ["autoCapture", "manualCapture", "backsidePending"].some(
              stateMachineState.matches
            )
          ) {
            stateMachineSend("CardCaptured");
          }
          GlobalLogger.debug(
            `scores above threshold: ${cardThreshold} lap threshold: ${laplacianThreshold}`,
            "detector"
          );

          if (debugMenu) {
            setBestScore((prev) => (score > prev ? score : prev));
            setBestLaplacian(laplacian);
            renderDebugCanvas(
              videoRefBg,
              frameDivRef,
              debugCanvasBestRef,
              debugMenu
            );
          }

          //TODO: see if this is still an issue.
          if (jpegUrl === undefined) {
            //lazy jpeg capture to workaround iOS GPU memory issue
            //downside is that we capture the submitted image ~10-15ms after detection but should be Ok
            const jpeg = generateImageFromCanvas(
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              flipHorizontal,
              flipVertical
            );
            addImageToBuffer(
              scanCountRef,
              jpeg!,
              score,
              laplacian,
              ret.modelClass,
              scanCaptureBuffer,
              scanProgress,
              setScanProgress,
              softMaxUploads,
              bufferSize,
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              cardThreshold,
              laplacianThreshold,
              videoMetadata,
              cardScanApi,
              cardIdRef,
              stateMachineState,
              onError
            );
          } else {
            addImageToBuffer(
              scanCountRef,
              jpegUrl,
              score,
              laplacian,
              ret.modelClass,
              scanCaptureBuffer,
              scanProgress,
              setScanProgress,
              softMaxUploads,
              bufferSize,
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              cardThreshold,
              laplacianThreshold,
              videoMetadata,
              cardScanApi,
              cardIdRef,
              stateMachineState,
              onError
            );
          }
        }
      }
    })
    .catch((error) => {
      _handleError(error, stateMachineSend, onError);
    });
};

const addImageToBuffer = (
  scanCountRef: React.MutableRefObject<number>,
  jpegUrl: string,
  score: number,
  laplacian: number,
  modelClass: string,
  scanCaptureBuffer: any,
  scanProgress: any,
  setScanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  threshold: any,
  laplacianThreshold: any,
  videoMetadata: any,
  cardScanApi: React.MutableRefObject<CardScanApi | null>,
  cardIdRef: any,
  stateMachineState: any,
  onError: any
) => {
  const file = dataURLtoFile(jpegUrl, "card.jpeg");
  scanCaptureBuffer.current.push({ file, score, laplacian });

  //TODO: update progress UI -- a little slow to update, we could use the scanCapture buffer.
  GlobalLogger.debug(
    `Adding image to capture buffer: ${scanProgress} lap: ${laplacian} score: ${score}`
  );

  setScanProgress((p: any) => {
    if (p >= (softMaxUploads * bufferSize) / 2) {
      return p + 0.5;
    } else {
      return p + 1;
    }
  });

  const numScans = Math.min(
    Math.ceil(scanProgress),
    softMaxUploads * bufferSize
  );

  const totalProgress = 20 + (numScans / (softMaxUploads * bufferSize)) * 100;

  document.documentElement.style.setProperty(
    "--cardscan-corner-height",
    `${totalProgress >= 100 ? 100 : totalProgress}%`
  );

  document.documentElement.style.setProperty(
    "--cardscan-corner-width",
    `${totalProgress >= 100 ? 100 : totalProgress}%`
  );

  document.documentElement.style.setProperty(
    "--cardscan-buffer-size",
    numScans.toString()
  );

  if (scanCaptureBuffer.current.length === bufferSize) {
    scanCountRef.current += 1;

    GlobalLogger.debug(
      `capture buffer full: ${scanCaptureBuffer.current.length}`
    );

    //pull best / least blurry image from buffer
    const lapScores = scanCaptureBuffer.current.map((i: any) =>
      Number(i.laplacian)
    );
    const maxScore = Math.max.apply(null, lapScores);
    const index = lapScores.indexOf(maxScore);

    const bestImage = scanCaptureBuffer.current[index].file;

    GlobalLogger.debug(`Best Image picked: ${index} - lap: ${maxScore}`);

    const environmentInfo = getEnvironmentInfo();
    const deviceInfo = getDeviceInfo();

    const videoElementInfo = getRefSize(videoRefBg);
    const guideFrameElementInfo = getRefSize(frameDivRef);

    //Note: metadata might have changed between capture and upload.
    const metadata = generateImageMetadata(
      CaptureType.automatic,
      videoRefBg,
      frameDivRef,
      captureCanvasRef,
      threshold,
      laplacianThreshold,
      videoMetadata,
      environmentInfo,
      videoElementInfo,
      guideFrameElementInfo,
      deviceInfo,
      modelClass
    );

    GlobalLogger.debug(JSON.stringify(metadata), "everything");

    const scores = scanCaptureBuffer.current.map((i: any) => ({
      score: i.score,
      laplacian: i.laplacian,
    }));

    metadata["capture_score"] = { scores: scores, max_lapScore: maxScore };

    uploadFile(
      scanCountRef.current,
      bestImage,
      CaptureType.automatic,
      metadata,
      cardScanApi,
      cardIdRef,
      stateMachineState,
      onError,
      () => {
        GlobalLogger.debug("image upload complete");
      }
    );

    //start capture buffer here so that we start the upload timer right away
    scanCaptureBuffer.current = [];
    GlobalLogger.debug("capture buffer cleared");
  }
};

const uploadFile = async (
  scanCount: number,
  file: File,
  captureType: CaptureType,
  metadata: { [key: string]: string },
  cardScanApi: React.MutableRefObject<CardScanApi | null>,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  onError: any,
  cb?: any
) => {
  const orientation = ["backsideProcessing", "backsidePending"].some(
    stateMachineState.matches
  )
    ? "back"
    : "front";

  const binaryData = await file.arrayBuffer();

  await cardScanApi.current
    ?.directUpload(orientation, captureType, cardIdRef.current, binaryData, {
      params: { scanCount },
    })
    .then(async (data) => {
      await cardScanApi.current?.setScanMetadata(data.scanId, {
        metadata,
        captureType,
        orientation,
      });

      if (cb) {
        cb();
      }
      GlobalLogger.debug("Upload Success");
    })
    .catch((error: any) => {
      if (error.response?.status === 204) {
        // Upload skipped - backend has enough data for this side
        const isFrontsideProcessing = stateMachineState.matches(
          "frontsideProcessing"
        );
        const isBacksideProcessing =
          stateMachineState.matches("backsideProcessing");

        if (isFrontsideProcessing) {
          GlobalLogger.debug("Upload skipped (204) for front side");
          stateMachineSend("upload.frontside_skipped");
        } else if (isBacksideProcessing) {
          GlobalLogger.debug("Upload skipped (204) for back side");
          stateMachineSend("upload.backside_skipped");
        } else {
          GlobalLogger.debug(
            `Upload skipped (204) ignored - current state: ${stateMachineState.value}`
          );
        }
        return;
      }
      _handleError(error, stateMachineSend, onError);
    });
};

//---------------------------------------
// Debug Menu
//---------------------------------------

//debug data to be passed in with every scan
const renderDebugCanvas = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  debugCanvasRef: React.RefObject<HTMLCanvasElement>,
  debugMenu?: boolean
) => {
  const videoBg = videoRefBg.current;
  if (debugMenu && debugCanvasRef && videoBg) {
    const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
      videoRefBg,
      frameDivRef
    );

    const dCanvas = debugCanvasRef.current;
    if (dCanvas) {
      const destCtx = dCanvas.getContext("2d", { alpha: false });
      destCtx?.drawImage(
        videoBg,
        guideX,
        guideY,
        guideWidth,
        guideHeight,
        0,
        0,
        dCanvas.width,
        dCanvas.height
      );
    }
  }
};
